import React from 'react';
import { PostResponseSchema } from '@api/client';
import { getContainerStyle, getWrapperStyle } from './utils/styles';
import CloseButton from './components/CloseButton';
import PostCardRecorded from './post-item-recorded/PostCardRecorded';
import PostCardEvent from './post-item-event/PostCardEvent';
import PostCardLivestream from './post-item-livestream/PostCardLivestream';
import { useIsMobile } from '@/hooks/useIsMobile';

interface Props {
  post: PostResponseSchema;
  onVideoClick?: (post: PostResponseSchema) => void;
  onImageClick?: (post: PostResponseSchema) => void;
  roundedBottom?: boolean;
  compact?: boolean;
  showCloseButton?: boolean;
  onClose?: () => void;
}

/**
 * Main orchestrator component for rendering post cards.
 * Routes to appropriate post type component based on post.post_type.
 */
export default function PostCard({
  post,
  onVideoClick,
  onImageClick,
  roundedBottom = false,
  compact = false,
  showCloseButton = false,
  onClose
}: Props): React.ReactElement {
  const isMobile = useIsMobile();

  // Generate styles
  const containerStyle = getContainerStyle({ roundedBottom, compact });
  const wrapperStyle = getWrapperStyle({ roundedBottom });

  // Route to appropriate post type component
  const renderPostContent = () => {
    switch (post.post_type) {
      case 'recorded':
        return (
          <PostCardRecorded
            post={post}
            onVideoClick={onVideoClick}
            onImageClick={onImageClick}
            wrapperStyle={wrapperStyle}
          />
        );
      case 'event':
        return (
          <PostCardEvent
            post={post}
            onVideoClick={onVideoClick}
            onImageClick={onImageClick}
            wrapperStyle={wrapperStyle}
          />
        );
      case 'livestream':
        return (
          <PostCardLivestream
            post={post}
            onVideoClick={onVideoClick}
            wrapperStyle={wrapperStyle}
          />
        );
      default:
        return (
          <PostCardRecorded
            post={post}
            onVideoClick={onVideoClick}
            onImageClick={onImageClick}
            wrapperStyle={wrapperStyle}
          />
        );
    }
  };

  const mediaContainerStyle: React.CSSProperties = {
    position: 'relative',
    width: '100%',
    height: isMobile ? '220px' : 'auto',
    aspectRatio: !isMobile ? '16 / 9' : undefined,
    margin: 0,
    overflow: 'hidden',
    ...containerStyle
  };

  return (
    <div style={mediaContainerStyle}>
      {/* Close button */}
      {showCloseButton && onClose && <CloseButton onClose={onClose} />}

      {/* Post type-specific content */}
      {renderPostContent()}
    </div>
  );
}
